import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import { transactionsActions } from 'app-components/flux/transactions';
import { getAvailableCredit } from 'app-components/flux/transactions/invoicesSelectors';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';
import { InvoiceSendUpsertAction } from 'components/InvoiceSendModal/actions';
import { buildApplyPaymentSubmenu } from 'components/TxnActionHelpers/buildApplyPaymentSubmenu';
import { type TxnOptionProps } from 'components/TxnActionHelpers/types';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useFormatAsCurrency from 'hooks/useFormatAsCurrency';
import { DateTime } from 'luxon';
import React from 'react';

export const usePaymentOptions = (
  props: TxnOptionProps & {
    /** Used if we only want to show payment related options. */
    paymentOnly?: boolean;
  },
) => {
  const { closeMenu, context, txnActionProps, paymentOnly } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const dispatch = useAppDispatch();

  const formatAsCurrency = useFormatAsCurrency();

  const availableCredit = useAppSelector((state) =>
    getAvailableCredit(state, {
      businessId: txn.businessId,
      customerId: txn.customerId,
      projectId: txn.projectId || 'no-project',
    }),
  );

  const options: any[] = [];

  if (
    txn.type !== 'INVOICE' ||
    (txn.subtype && txn.subtype !== 'INVOICE' && txn.subtype !== 'INVOICE_CREDIT_MEMO' && txn.subtype !== 'ESTIMATE') ||
    attributes.txnToEditWasFlipped
  ) {
    return options;
  }

  const recordPayment = txn.subtype !== 'INVOICE_CREDIT_MEMO';
  const isEstimate = txn.subtype === 'ESTIMATE';

  if (!attributes.txnToEditWasFlipped && attributes.hasAllocations && !isEstimate) {
    options.push({
      id: 'txn-menu-edit-invoice-allocations',
      key: 'txn-menu-edit-invoice-allocations',
      startIcon: <PaymentsOutlinedIcon />,
      label: 'Edit assignments',
      onSelect: () => {
        closeMenu();
        dispatch(
          InvoicePaymentUpsertAction({
            action: 'EDIT_ALLOCATIONS',
            context,
            initId: txn.id,
            initScreen: 'MAIN',
            initCustomerId: txn.customerId,
          }),
        );
      },
    });
  }

  if (!attributes.isFullyAllocated && !isEstimate) {
    if (!attributes.hasAllocations && txn.subtype === 'INVOICE_CREDIT_MEMO') {
      options.push({
        id: 'txn-menu-apply-to-invoice',
        key: 'txn-menu-apply-to-invoice',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply to invoice',
        onSelect: () => {
          closeMenu();
          dispatch(
            InvoicePaymentUpsertAction({
              action: 'EDIT_ALLOCATIONS',
              context,
              initId: txn.id,
              initScreen: 'MAIN',
            }),
          );
        },
      });
    }
    if (!isEstimate) {
      const subMenu = buildApplyPaymentSubmenu({
        dispatch,
        recordPayment,
        context,
        txnId: txn.id,
        closeMenu,
        availableCredit,
        formatAsCurrency,
      }) as any[];

      const recordOptions = {
        id: 'txn-menu-allocate',
        key: 'txn-menu-allocate',
        startIcon: recordPayment ? <PaymentsOutlinedIcon /> : <UndoIcon />,
        label: recordPayment ? 'Apply payment' : 'Refund',
        subMenu,
      };

      options.push(recordOptions);
    }
  }

  // add if more than one edit/record menu item
  if (options.length > 1 && !paymentOnly) {
    options[options.length - 1].divider = true;
  }

  if (!txn.sentOn && !paymentOnly) {
    options.push({
      id: 'mark-as-sent',
      label: 'Send',
      startIcon: <SendIcon />,
      subMenu: [
        {
          label: 'Mark as sent',
          onSelect: () => {
            closeMenu();
            dispatch(transactionsActions.updateTransaction(txn.set('sentOn', DateTime.now().toISODate())), {
              context,
              undo: { userMessage: 'Transaction updated.' },
            });
          },
        },
        {
          label: 'Send via email',
          onSelect: () => {
            closeMenu();
            dispatch(
              InvoiceSendUpsertAction({
                context,
                formComponentProps: { invoice: txn },
              }),
            );
          },
        },
      ],
    });
  }

  // add divider on last option
  if (options.length > 0 && !paymentOnly && !isEstimate) {
    options[options.length - 1].divider = true;
  }

  return options;
};
